import { useCallback } from 'react';
import { Form, FormRenderProps, RenderableProps } from 'react-final-form';
import { Box, Typography } from '@mui/material';

import { ConfirmDialog, HelpTip, LinearProgress } from '@occc-app/design-system';
import { Retailer_Network_User_Role, RetailerNetworkLocationGroupFragment } from '@occc/graphql';
import { pluralize } from '@occc-app/lib/strings';

import { AssociateUsersWithLocationGroupsFormValues } from './associate-users-with-location-groups-dialog.types';
import { validate } from './associate-users-with-location-groups-dialog.validation';

import { RetailerNetworkUserMultipleSelectField } from '../retailer-network-user-multiple-select-field.component';
import { RetailerNetworkLocationGroupMultipleSelectField } from '../retailer-network-location-group-multiple-select-field.component';
import { useRetailerNetworkUserSelector } from '../../retailer-network-context/use-retailer-network-user.selector';

type AssociateUsersWithLocationGroupDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AssociateUsersWithLocationGroupsFormValues) => void;
  loading: boolean;
  retailerNetworkUuid: string;
  locationGroup: RetailerNetworkLocationGroupFragment;
};

export const AssociateUsersWithLocationGroupsDialog = ({
  open,
  onClose,
  onSubmit,
  loading,
  retailerNetworkUuid,
  locationGroup,
}: AssociateUsersWithLocationGroupDialogProps) => {
  const { retailerNetworkUserOpenCommerceUserUuid, isRetailerNetworkSiteAdmin } =
    useRetailerNetworkUserSelector();

  const renderForm = useCallback<
    NonNullable<
      RenderableProps<FormRenderProps<AssociateUsersWithLocationGroupsFormValues>>['render']
    >
  >(
    ({ handleSubmit, valid, values }) => {
      const countOfUsers = values.users?.length;
      const confirmButtonText = `Add ${countOfUsers || ''} ${pluralize(countOfUsers, 'user')}`;

      return (
        <ConfirmDialog
          dialogTitle='Choose Users to Add'
          onClose={onClose}
          open={open}
          fullWidth
          maxWidth='sm'
          confirmButtonText={confirmButtonText}
          confirmButtonProps={{
            onClick: handleSubmit,
            disabled: !valid || loading,
          }}
        >
          {loading && <LinearProgress />}
          <HelpTip title='Adding Users to Location Group Only'>
            <Typography variant='body2' color='textSecondary'>
              To add Users or Admins to the entire Network, add them from the Network Details page.
            </Typography>
          </HelpTip>
          <Box mt={1}>
            <RetailerNetworkUserMultipleSelectField
              name='users'
              label='Select Users'
              retailerNetworkUuidEquals={retailerNetworkUuid}
              roleIn={
                isRetailerNetworkSiteAdmin ? [Retailer_Network_User_Role.SiteUser] : undefined
              }
            />
            <RetailerNetworkLocationGroupMultipleSelectField
              name='locationGroups'
              label='Select Location Groups'
              retailerNetworkUuidEquals={retailerNetworkUuid}
              openCommerceUserUuidEquals={
                isRetailerNetworkSiteAdmin ? retailerNetworkUserOpenCommerceUserUuid : undefined
              }
            />
          </Box>
        </ConfirmDialog>
      );
    },
    [
      loading,
      onClose,
      open,
      retailerNetworkUuid,
      isRetailerNetworkSiteAdmin,
      retailerNetworkUserOpenCommerceUserUuid,
    ],
  );

  return (
    <Form<AssociateUsersWithLocationGroupsFormValues>
      onSubmit={onSubmit}
      validate={validate}
      render={renderForm}
      initialValues={{ locationGroups: [locationGroup] }}
    />
  );
};
